import React, {createContext, useCallback, useContext, useMemo, useReducer} from 'react';
import {parkingBansReducer} from './parkingBansReducer';
import useSafeDispatch from '../../hooks/useSafeDispatch';
import {actionTypes, initialState} from './parkingBansReducer';

export const PER_PAGE = 10;

// 1. Context creation
const ParkingBansContext = createContext(null);
ParkingBansContext.displayName = 'ParkingBansContext';

const ParkingBansDispatch = createContext(null);
ParkingBansDispatch.displayName = 'ParkingBansDispatch';

// 2. Content provider
const ParkingBansProvider = ({children, reducer = parkingBansReducer}) => {
  const [state, unsafeDispatch] = useReducer(reducer, initialState);

  const memoizedState = useMemo(() => state, [state]);
  const dispatch = useSafeDispatch(unsafeDispatch);

  return (
    <ParkingBansContext.Provider value={memoizedState}>
      <ParkingBansDispatch.Provider value={{dispatch}}>{children}</ParkingBansDispatch.Provider>
    </ParkingBansContext.Provider>
  );
};

// 3. custom hooks
function useParkingBansState() {
  const context = useContext(ParkingBansContext);

  if (context === undefined) {
    throw new Error('useParkingBansState must be used within a ParkingBansProvider');
  }
  return context;
}

function useParkingBansDispatch() {
  const context = useContext(ParkingBansDispatch);

  if (context === undefined) {
    throw new Error('useParkingBansDispatch must be used within a ParkingBansProvider');
  }

  const {dispatch} = context;

  const setBannedParkingEmployees = useCallback(
    (payload) => dispatch({type: actionTypes.setBannedParkingEmployees, payload}),
    [dispatch]
  );

  const setCurrentPage = useCallback(
    (payload) => dispatch({type: actionTypes.setCurrentPage, payload}),
    [dispatch]
  );

  const setTotalPages = useCallback(
    (payload) => dispatch({type: actionTypes.setTotalPages, payload}),
    [dispatch]
  );

  const setCurrentSearch = useCallback(
    (payload) => dispatch({type: actionTypes.setCurrentSearch, payload}),
    [dispatch]
  );

  const setCurrentStatus = useCallback(
    (payload) => dispatch({type: actionTypes.setCurrentStatus, payload}),
    [dispatch]
  );

  return {
    dispatch,
    setBannedParkingEmployees,
    setTotalPages,
    setCurrentPage,
    setCurrentSearch,
    setCurrentStatus
  };
}

export {ParkingBansProvider, useParkingBansState, useParkingBansDispatch};
